import { useField } from "formik";
import type { ReactNode } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface SelectFieldProps {
  name: string;
  label: string;
  options: { value: string; label: string }[];
  placeholder?: string;
}

/** Formik-bound shadcn Select (no autosave — the step saves on Continue). */
export function SelectField({ name, label, options, placeholder }: SelectFieldProps) {
  const [field, meta, helpers] = useField<string>(name);
  const hasError = meta.touched && !!meta.error;
  return (
    <div className="grid gap-2">
      <Label htmlFor={name}>{label}</Label>
      <Select
        value={field.value || ""}
        onValueChange={(value) => {
          helpers.setValue(value);
          helpers.setTouched(true);
        }}
      >
        <SelectTrigger id={name} className="w-full" aria-invalid={hasError}>
          <SelectValue placeholder={placeholder ?? "Select…"} />
        </SelectTrigger>
        <SelectContent position="popper">
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {hasError && <p className="text-destructive text-sm">{meta.error}</p>}
    </div>
  );
}

interface CheckboxFieldProps {
  name: string;
  label: ReactNode;
}

/** Formik-bound shadcn Checkbox that stores a boolean. */
export function CheckboxField({ name, label }: CheckboxFieldProps) {
  const [field, , helpers] = useField<boolean>(name);
  return (
    <div className="flex items-center gap-2">
      <Checkbox
        id={name}
        checked={!!field.value}
        onCheckedChange={(checked) => helpers.setValue(checked === true)}
      />
      <Label htmlFor={name} className="font-normal">
        {label}
      </Label>
    </div>
  );
}
